import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotificationStore, NotificationType } from '@/stores/notificationStore';
import { X, AlertCircle, Info } from 'lucide-react';

const icons: Record<NotificationType, React.ReactNode> = {
    success: (
        <div className="relative w-10 h-10 flex items-center justify-center">
            <img
                src="/assets/flask-only-no-bg.png"
                alt="success"
                className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(255,215,0,0.6)]"
            />
        </div>
    ),
    error: <AlertCircle className="text-red-400" size={24} />,
    info: <Info className="text-blue-400" size={24} />,
};

const borderColors: Record<NotificationType, string> = {
    success: 'border-gold-500/30',
    error: 'border-red-500/30',
    info: 'border-blue-500/30',
};

export const Toaster: React.FC = () => {
    const { notifications, removeNotification } = useNotificationStore();

    return (
        <div className="fixed bottom-6 left-6 z-[9999] flex flex-col gap-3 pointer-events-none max-w-md w-full sm:max-w-sm" dir="rtl">
            <AnimatePresence mode="popLayout">
                {notifications.map((n) => (
                    <motion.div
                        key={n.id}
                        layout
                        initial={{ opacity: 0, x: -50, scale: 0.9 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: -20, scale: 0.95 }}
                        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                        className={`pointer-events-auto relative group overflow-hidden glass-card p-4 rounded-2xl flex items-center gap-4 border ${borderColors[n.type]} backdrop-blur-xl bg-mystic-900/60 shadow-[0_15px_35px_rgba(0,0,0,0.3)]`}
                    >
                        {/* Glow effect */}
                        <div className={`absolute -inset-1 opacity-20 group-hover:opacity-30 blur-2xl transition-opacity bg-gradient-to-r ${n.type === 'success' ? 'from-gold-600/40' : n.type === 'error' ? 'from-red-600/40' : 'from-blue-600/40'
                            }`} />

                        <div className="relative z-10 flex-shrink-0">
                            {icons[n.type]}
                        </div>

                        <div className="relative z-10 flex-1">
                            <p className="text-sm font-medium text-mystic-100 leading-tight">
                                {n.message}
                            </p>
                        </div>

                        <button
                            onClick={() => removeNotification(n.id)}
                            className="relative z-10 p-1.5 hover:bg-white/10 rounded-xl transition-colors text-mystic-400 hover:text-white"
                        >
                            <X size={16} />
                        </button>

                        {n.duration !== undefined && n.duration !== Infinity && (
                            <motion.div
                                initial={{ width: '100%' }}
                                animate={{ width: '0%' }}
                                transition={{ duration: (n.duration as number) / 1000, ease: 'linear' }}
                                className={`absolute bottom-0 left-0 h-0.5 opacity-50 ${n.type === 'success' ? 'bg-gold-500' : n.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
                                    }`}
                            />
                        )}
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};
